/**
 * The story layer at runtime: what a player can hear, say, and conclude, given what they hold.
 *
 * This is a read model over the pack plus the player's state. It computes; it never stores.
 * `investigation.ts` owns the state and calls in here to ask what that state means, which keeps
 * "what is true of this save" testable without a save, a server or a browser.
 */
import type {
  ContentPack,
  LocaleTag,
  PackDialogue,
  PackDialogueNode,
  PackDialogueOption,
  PackEnding,
  PackStory,
  PackSignalSource,
} from '../content/schema.js';
import { localized } from '../content/schema.js';
import { applyEffects, gatePasses, type StoryState } from './gates.js';

export interface ResolvedClue {
  id: string;
  label: string;
  description: string;
  thread?: string;
}

export interface ResolvedThread {
  id: string;
  label: string;
  /** Clue ids belonging to this thread, in pack order. */
  clueIds: string[];
}

export interface ResolvedOption {
  id: string;
  label: string;
  /**
   * Authored phrasings for this option, resolved for the active locale. Sent to the matcher so
   * it scores against what the author expected a player to *say*, not just the button text.
   */
  intents: string[];
  next?: string;
  option: PackDialogueOption;
}

export interface ResolvedNode {
  id: string;
  line: string;
  /**
   * The pre-rendered reading of `line`, if the author has recorded one.
   *
   * Resolved by the loader, because that is the only layer that knows the pack's root and the
   * active locale. Absent while a conversation is still text-only, which is the normal state
   * during authoring and must never be treated as an error.
   */
  clipUrl?: string;
  options: ResolvedOption[];
  next?: string;
  node: PackDialogueNode;
  /** True when the node neither offers a reachable option nor points anywhere: the end. */
  terminal: boolean;
}

/**
 * A pack's story, with every string already resolved for one locale.
 *
 * Built once per session. Nothing downstream — UI, investigation state, the dialogue runner —
 * touches a `LocalizedText` again.
 */
export class Story {
  readonly id: string;
  readonly title: string;
  readonly premise?: string;

  private readonly locale: LocaleTag;
  private readonly defaultLocale: LocaleTag;
  private readonly raw: PackStory;
  private readonly resolveClip: ((source: PackSignalSource) => string | null) | undefined;
  private readonly cluesById = new Map<string, ResolvedClue>();
  private readonly threads: ResolvedThread[] = [];
  private readonly dialoguesById = new Map<string, PackDialogue>();
  private readonly nodesByDialogue = new Map<string, Map<string, PackDialogueNode>>();

  constructor(
    story: PackStory,
    locale: LocaleTag,
    defaultLocale: LocaleTag,
    resolveClip?: (source: PackSignalSource) => string | null,
  ) {
    this.resolveClip = resolveClip;
    this.raw = story;
    this.locale = locale;
    this.defaultLocale = defaultLocale;
    this.id = story.id;
    this.title = this.text(story.title) ?? story.id;
    const premise = this.text(story.premise);
    if (premise) this.premise = premise;

    for (const clue of story.clues ?? []) {
      this.cluesById.set(clue.id, {
        id: clue.id,
        label: this.text(clue.label) ?? clue.id,
        description: this.text(clue.description) ?? '',
        ...(clue.thread ? { thread: clue.thread } : {}),
      });
    }

    // Declared threads first, in pack order, then a catch-all for clues with no thread — so the
    // truth surface always accounts for every clue rather than quietly dropping the loose ones.
    for (const thread of story.threads ?? []) {
      this.threads.push({
        id: thread.id,
        label: this.text(thread.label) ?? thread.id,
        clueIds: (story.clues ?? [])
          .filter((clue) => clue.thread === thread.id)
          .map((clue) => clue.id),
      });
    }
    const loose = (story.clues ?? []).filter(
      (clue) => !clue.thread || !this.threads.some((thread) => thread.id === clue.thread),
    );
    if (loose.length > 0) {
      this.threads.push({ id: '', label: '', clueIds: loose.map((clue) => clue.id) });
    }

    for (const dialogue of story.dialogues ?? []) {
      this.dialoguesById.set(dialogue.id, dialogue);
      this.nodesByDialogue.set(dialogue.id, new Map(dialogue.nodes.map((node) => [node.id, node])));
    }
  }

  private text(value: Parameters<typeof localized>[0]): string | undefined {
    return localized(value, this.locale, this.defaultLocale);
  }

  get allClues(): ResolvedClue[] {
    return [...this.cluesById.values()];
  }

  get allThreads(): ResolvedThread[] {
    return this.threads;
  }

  clue(id: string): ResolvedClue | undefined {
    return this.cluesById.get(id);
  }

  dialogue(id: string): PackDialogue | undefined {
    return this.dialoguesById.get(id);
  }

  node(dialogueId: string, nodeId: string): PackDialogueNode | undefined {
    return this.nodesByDialogue.get(dialogueId)?.get(nodeId);
  }

  /** Clues the player holds, in pack order — the clue board reads this. */
  heldClues(state: StoryState): ResolvedClue[] {
    return this.allClues.filter((clue) => state.clues.has(clue.id));
  }

  /**
   * Resolve a node for display: its line, and the options actually available to this player.
   *
   * A node whose own gate fails still resolves — the caller decides whether to fall through to
   * `next` — but its gated options are filtered out entirely. A player should never see a choice
   * they cannot take; that is a menu, not a conversation.
   */
  resolveNode(dialogueId: string, nodeId: string, state: StoryState): ResolvedNode | undefined {
    const node = this.node(dialogueId, nodeId);
    if (!node) return undefined;

    const options: ResolvedOption[] = (node.options ?? [])
      .filter((option) => gatePasses(option.requires, state))
      .map((option) => ({
        id: option.id,
        label: this.text(option.label) ?? option.id,
        intents: (option.intents ?? [])
          .map((intent) => this.text(intent))
          .filter((intent): intent is string => Boolean(intent)),
        ...(option.next ? { next: option.next } : {}),
        option,
      }));

    const clipUrl = node.source ? this.resolveClip?.(node.source) : null;

    return {
      id: node.id,
      line: this.text(node.line) ?? '',
      ...(clipUrl ? { clipUrl } : {}),
      options,
      ...(node.next ? { next: node.next } : {}),
      node,
      terminal: options.length === 0 && node.next === undefined,
    };
  }

  /**
   * The ending the player has reached, if any.
   *
   * First match in declaration order wins, so authors put the most specific ending first — the
   * same rule as a switch statement, and one an author can hold in their head.
   */
  endingFor(state: StoryState):
    | (PackEnding & {
        resolvedLabel: string;
        resolvedText: string;
        resolvedClipUrl: string | null;
      })
    | null {
    for (const ending of this.raw.endings ?? []) {
      if (!gatePasses(ending.requires, state)) continue;
      return {
        ...ending,
        resolvedLabel: this.text(ending.label) ?? ending.id,
        resolvedText: this.text(ending.text) ?? '',
        resolvedClipUrl: ending.source ? (this.resolveClip?.(ending.source) ?? null) : null,
      };
    }
    return null;
  }

  /**
   * How close the player is, as clue counts per thread.
   *
   * Deliberately *not* "you are 60% done". A mystery that reports a completion percentage stops
   * being a mystery; what this gives is "you have two of the four things about the tower", which
   * tells a player where to look without telling them what they will find.
   */
  progress(state: StoryState): {
    threads: Array<{ id: string; label: string; held: number; total: number }>;
    cluesHeld: number;
    cluesTotal: number;
    ending: string | null;
  } {
    const threads = this.threads.map((thread) => ({
      id: thread.id,
      label: thread.label,
      held: thread.clueIds.filter((id) => state.clues.has(id)).length,
      total: thread.clueIds.length,
    }));

    const ending = this.endingFor(state);
    return {
      threads,
      cluesHeld: this.allClues.filter((clue) => state.clues.has(clue.id)).length,
      cluesTotal: this.allClues.length,
      ending: ending ? ending.id : null,
    };
  }
}

/** Build the story layer from a pack, or null for a pack that tells no story (a v1 pack). */
export function buildStory(
  pack: ContentPack,
  locale: LocaleTag,
  defaultLocale: LocaleTag,
  resolveClip?: (source: PackSignalSource) => string | null,
): Story | null {
  return pack.story ? new Story(pack.story, locale, defaultLocale, resolveClip) : null;
}

/**
 * Walk from a node to the first one the player can actually be shown.
 *
 * A node whose gate fails is skipped rather than shown empty — the conversation falls through to
 * its `next`. `seen` breaks cycles: an authoring mistake should end the conversation, not hang
 * the browser. The validator reports the cycle separately.
 */
export function advanceToPlayableNode(
  story: Story,
  dialogueId: string,
  startNodeId: string,
  state: StoryState,
): { node: ResolvedNode | undefined; state: StoryState; visited: string[] } {
  const visited: string[] = [];
  let currentId: string | undefined = startNodeId;
  let currentState = state;

  while (currentId && !visited.includes(currentId)) {
    visited.push(currentId);
    const raw = story.node(dialogueId, currentId);
    if (!raw) break;

    if (!gatePasses(raw.requires, currentState)) {
      currentId = raw.next;
      continue;
    }

    // Arriving at a node is what grants its effects — before its options are resolved, since an
    // option may be gated on what this very node just gave the player.
    currentState = applyEffects(currentState, raw.effects);
    const resolved = story.resolveNode(dialogueId, currentId, currentState);
    return { node: resolved, state: currentState, visited };
  }

  return { node: undefined, state: currentState, visited };
}
